import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FolderOpen, 
  MessageCircle, 
  User, 
  Globe, 
  Mail, 
  Terminal, 
  Settings,
  Trash2,
  Calendar,
  Image,
  Video,
  BookOpen,
  Store,
  Wifi,
  Battery,
  Volume2,
  Play
} from 'lucide-react';

const DockContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
`;

const DockIcon = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isActive ? 'rgba(0, 122, 255, 0.2)' : 'transparent'};
  
  &:hover {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${props => props.color || '#333'};
  font-size: 20px;
`;

const AppIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${props => props.background || 'transparent'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.color || '#333'};
  background-image: ${props => props.gradient || 'none'};
`;

const ActiveIndicator = styled.div`
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background: #007AFF;
  border-radius: 50%;
  opacity: ${props => props.isActive ? 1 : 0};
  transition: opacity 0.2s ease;
`;

const Tooltip = styled(motion.div)`
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1001;
  font-weight: 500;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const Dock = ({ onAppClick, openWindows }) => {
  const [hoveredApp, setHoveredApp] = useState(null);

  const apps = [
    { 
      id: 'finder', 
      name: 'Finder', 
      icon: <FolderOpen size={20} />, 
      type: 'finder', 
      title: 'Finder',
      color: '#007AFF',
      functional: true
    },
    { 
      id: 'messages', 
      name: 'Messages', 
      icon: <MessageCircle size={20} />, 
      type: 'messages', 
      title: 'Messages',
      color: '#34C759',
      functional: true
    },
    { 
      id: 'about', 
      name: 'About Me', 
      icon: <User size={20} />, 
      type: 'about', 
      title: 'About Me',
      color: '#007AFF',
      functional: true
    },
    { 
      id: 'safari', 
      name: 'Safari', 
      icon: <Globe size={20} />, 
      type: 'safari', 
      title: 'Safari',
      color: '#007AFF',
      functional: false
    },
    { 
      id: 'mail', 
      name: 'Mail', 
      icon: <Mail size={20} />, 
      type: 'mail', 
      title: 'Mail',
      color: '#007AFF',
      functional: false
    },
    { 
      id: 'photos', 
      name: 'Photos', 
      icon: <Image size={20} />, 
      type: 'photos', 
      title: 'Photos',
      color: '#FF9500',
      functional: false
    },
    { 
      id: 'calendar', 
      name: 'Calendar', 
      icon: <Calendar size={20} />, 
      type: 'calendar', 
      title: 'Calendar',
      color: '#FF3B30',
      functional: false
    },
    { 
      id: 'tv', 
      name: 'TV', 
      icon: <Video size={20} />, 
      type: 'tv', 
      title: 'TV',
      color: '#000000',
      functional: false
    },
    { 
      id: 'books', 
      name: 'Books', 
      icon: <BookOpen size={20} />, 
      type: 'books', 
      title: 'Books',
      color: '#FF9500',
      functional: false
    },
    { 
      id: 'appstore', 
      name: 'App Store', 
      icon: <Store size={20} />, 
      type: 'appstore', 
      title: 'App Store',
      color: '#007AFF',
      functional: false
    },
    { 
      id: 'settings', 
      name: 'Settings', 
      icon: <Settings size={20} />, 
      type: 'settings', 
      title: 'Settings',
      color: '#8E8E93',
      functional: false
    },
    { 
      id: 'spotify', 
      name: 'Spotify', 
      icon: <Play size={20} />, 
      type: 'music', 
      title: 'Music',
      color: '#1DB954',
      functional: true
    },
    { 
      id: 'folder', 
      name: 'Folder', 
      icon: <FolderOpen size={20} />, 
      type: 'folder', 
      title: 'Folder',
      color: '#007AFF',
      functional: false
    },
    { 
      id: 'trash', 
      name: 'Trash', 
      icon: <Trash2 size={20} />, 
      type: 'trash', 
      title: 'Trash',
      color: '#8E8E93',
      functional: true
    }
  ];

  const handleAppClick = (app) => {
    if (app.functional) {
      onAppClick(app.type, app.title, app.props || {});
    }
    // Dummy apps don't do anything
  };

  const isAppOpen = (appType) => {
    return openWindows.some(window => window.type === appType);
  };

  return (
    <DockContainer>
      <AnimatePresence>
        {apps.map((app) => (
          <DockIcon
            key={app.id}
            isActive={isAppOpen(app.type)}
            onClick={() => handleAppClick(app)}
            onMouseEnter={() => setHoveredApp(app.id)}
            onMouseLeave={() => setHoveredApp(null)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconWrapper color={app.color}>
              {app.icon}
            </IconWrapper>
            <ActiveIndicator isActive={isAppOpen(app.type)} />
            
            {hoveredApp === app.id && (
              <Tooltip
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
              >
                {app.functional ? app.name : `${app.name} (Dummy)`}
              </Tooltip>
            )}
          </DockIcon>
        ))}
      </AnimatePresence>
    </DockContainer>
  );
};

export default Dock;
